import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth, can } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Wrench, Upload, Trash2, ArrowRight, ShieldCheck } from "lucide-react";
import MaintenanceDialog from "@/components/MaintenanceDialog";
import ProviderDialog from "@/components/ProviderDialog";
import { formatUSD } from "@/components/KPICard";
import { formatDMY } from "@/lib/dates";
import { MAINTENANCE } from "@/constants/testIds";
import { toast } from "sonner";

const STATUS_META = {
  scheduled: { label: "Programado", tone: "bg-sky-500/10 text-sky-500 border-sky-500/20" },
  in_progress: { label: "En curso", tone: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  done: { label: "Finalizado", tone: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  cancelled: { label: "Cancelado", tone: "bg-secondary text-muted-foreground border-border" },
};

const TYPE_LABEL = { repair: "Reparación", preventive: "Preventivo", inspection: "Inspección", improvement: "Mejora" };

function PhotoUploader({ maintenanceId, kind, onUploaded }) {
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      await api.post(`/maintenance/${maintenanceId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto subida");
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      e.target.value = "";
    }
  };
  return (
    <label className="cursor-pointer inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border/60 hover:border-foreground/30">
      <Upload className="w-3 h-3" /> {kind === "before" ? "Antes" : "Después"}
      <input type="file" accept="image/*" className="hidden" onChange={onChange} data-testid={`${MAINTENANCE.photoUpload}-${kind}`} />
    </label>
  );
}

function PhotoThumb({ id, onDelete }) {
  const token = localStorage.getItem("access_token");
  const url = `${API}/maintenance/photo/${id}?t=${token?.slice(0,10)}`;
  // Note: photo endpoint requires cookie auth (or bearer). Since <img> can send cookies with credentials=include implicitly only same-origin, we rely on the httpOnly cookie.
  return (
    <div className="relative group">
      <img src={url} alt="mnt" className="w-20 h-20 rounded-md object-cover border border-border/60" />
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute -top-2 -right-2 hidden group-hover:flex w-5 h-5 rounded-full bg-rose-500 text-white items-center justify-center text-xs"
        ><Trash2 className="w-3 h-3" /></button>
      )}
    </div>
  );
}

function MaintenanceRow({ m, property, provider, onEdit, onDelete, onReload, canEdit }) {
  const st = STATUS_META[m.status] || STATUS_META.scheduled;
  const [expanded, setExpanded] = useState(false);
  const deletePhoto = async (gid) => {
    try {
      await api.delete(`/maintenance/${m.id}/photos/${gid}`);
      toast.success("Foto eliminada");
      onReload?.();
    } catch { toast.error("No se pudo eliminar la foto"); }
  };
  return (
    <div className="rounded-xl border border-border/60 bg-card">
      <div className="p-4 sm:p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center shrink-0">
          <Wrench className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium">{m.title}</div>
            <span className={`country-badge ${st.tone}`}>{st.label}</span>
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{TYPE_LABEL[m.type] || m.type}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {property?.name || m.property_id?.slice(0, 8)} · {provider?.name || "Sin proveedor"}
            {m.scheduled_date && ` · Programado ${formatDMY(m.scheduled_date)}`}
            {m.completed_date && ` · Completado ${formatDMY(m.completed_date)}`}
            {m.warranty_until && (
              <span className="inline-flex items-center gap-1 ml-2 text-emerald-500">
                <ShieldCheck className="w-3 h-3" /> garantía hasta {formatDMY(m.warranty_until)}
              </span>
            )}
          </div>
          {m.description && <div className="text-sm text-muted-foreground mt-2 line-clamp-2">{m.description}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-lg font-semibold num">{formatUSD(m.cost_usd || 0)}</div>
          <div className="text-[11px] text-muted-foreground num">
            {m.cost} {m.currency}
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-5 pb-4 border-t border-border/60 pt-3 flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={() => setExpanded((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
          Fotos ({(m.before_photos?.length || 0) + (m.after_photos?.length || 0)})
        </button>
        {canEdit && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={onEdit}>Editar</Button>
            <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-500" onClick={onDelete}>Eliminar</Button>
          </div>
        )}
      </div>
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Antes</div>
              {canEdit && <PhotoUploader maintenanceId={m.id} kind="before" onUploaded={onReload} />}
            </div>
            <div className="flex gap-2 flex-wrap">
              {(m.before_photos || []).map((gid) => (
                <PhotoThumb key={gid} id={gid} onDelete={canEdit ? () => deletePhoto(gid) : null} />
              ))}
              {!m.before_photos?.length && <div className="text-xs text-muted-foreground">Sin fotos.</div>}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Después</div>
              {canEdit && <PhotoUploader maintenanceId={m.id} kind="after" onUploaded={onReload} />}
            </div>
            <div className="flex gap-2 flex-wrap">
              {(m.after_photos || []).map((gid) => (
                <PhotoThumb key={gid} id={gid} onDelete={canEdit ? () => deletePhoto(gid) : null} />
              ))}
              {!m.after_photos?.length && <div className="text-xs text-muted-foreground">Sin fotos.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Maintenance() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [providers, setProviders] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [provDlgOpen, setProvDlgOpen] = useState(false);

  const load = async () => {
    const [m, p, pr] = await Promise.all([
      api.get("/maintenance"), api.get("/properties"), api.get("/providers"),
    ]);
    setItems(m.data);
    setProperties(p.data);
    setProviders(pr.data);
  };
  useEffect(() => { load(); }, []);

  const propMap = Object.fromEntries(properties.map((p) => [p.id, p]));
  const provMap = Object.fromEntries(providers.map((p) => [p.id, p]));
  const canEdit = can(user, "manageProperties");

  const del = async (id) => {
    if (!window.confirm("¿Eliminar mantenimiento?")) return;
    await api.delete(`/maintenance/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Mantenimiento</h1>
          <div className="text-sm text-muted-foreground mt-1">{items.length} intervenciones · {providers.length} proveedores.</div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setProvDlgOpen(true)}>Gestionar proveedores</Button>
          )}
          {canEdit && (
            <Button className="rounded-full" onClick={() => { setEditing(null); setDlgOpen(true); }} data-testid={MAINTENANCE.addBtn}>
              <Plus className="w-4 h-4 mr-1" /> Nuevo mantenimiento
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3" data-testid={MAINTENANCE.list}>
        {items.map((m) => (
          <MaintenanceRow
            key={m.id}
            m={m}
            property={propMap[m.property_id]}
            provider={provMap[m.provider_id]}
            canEdit={canEdit}
            onEdit={() => { setEditing(m); setDlgOpen(true); }}
            onDelete={() => del(m.id)}
            onReload={load}
          />
        ))}
        {!items.length && <div className="text-center py-16 text-muted-foreground">Sin mantenimientos registrados.</div>}
      </div>

      <MaintenanceDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        properties={properties}
        providers={providers}
        item={editing}
        onSaved={load}
      />
      <ProviderDialog open={provDlgOpen} onOpenChange={setProvDlgOpen} providers={providers} onSaved={load} />
    </div>
  );
}
